import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AdminNewsTab from '../components/AdminNewsTab';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    Shield,
    Users,
    Ticket,
    CreditCard,
    Plus,
    Trash2,
    Loader2,
    Check,
    X,
    RefreshCw,
    Crown,
    Gift,
    TrendingUp,
    Clock,
    Percent,
    DollarSign,
    UserCheck,
    Copy,
    Bug,
    AlertCircle,
    CheckCircle2,
    XCircle,
    MessageSquare,
    ClipboardList,
    Mail,
    FileDown,
    ShieldCheck,
    Send,
    MailOpen,
    MousePointer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [exportingAudit, setExportingAudit] = useState(false);
    const [auditStartDate, setAuditStartDate] = useState('');
    const [auditEndDate, setAuditEndDate] = useState('');
    const [auditEmail, setAuditEmail] = useState('');
    const [emailStats, setEmailStats] = useState(null);
    
    // Stats
    const [stats, setStats] = useState(null);
    
    // Promo codes
    const [promoCodes, setPromoCodes] = useState([]);
    const [promoLoading, setPromoLoading] = useState(false);
    
    // New promo form
    const [newPromoCode, setNewPromoCode] = useState('');
    const [newPromoDiscount, setNewPromoDiscount] = useState('');
    const [newPromoTier, setNewPromoTier] = useState('all');
    const [newPromoMaxUses, setNewPromoMaxUses] = useState('');
    const [creatingPromo, setCreatingPromo] = useState(false);
    
    // Redemptions
    const [redemptions, setRedemptions] = useState([]);
    
    // Purchases
    const [purchases, setPurchases] = useState([]);
    
    // Users
    const [users, setUsers] = useState([]);
    const [userFilter, setUserFilter] = useState('all');
    
    // Issues
    const [issues, setIssues] = useState([]);
    const [issueFilter, setIssueFilter] = useState('all');
    const [updatingIssue, setUpdatingIssue] = useState(null);
    
    // Waitlist
    const [waitlist, setWaitlist] = useState([]);
    const [waitlistLoading, setWaitlistLoading] = useState(false);

    const fetchStats = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/stats`);
            setStats(res.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    }, []);

    const fetchEmailStats = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/email-engagement/stats`);
            setEmailStats(res.data);
        } catch (error) {
            console.error('Failed to fetch email engagement stats:', error);
        }
    }, []);

    const fetchPromoCodes = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/promo-codes`);
            setPromoCodes(res.data);
        } catch (error) {
            console.error('Failed to fetch promo codes:', error);
        }
    }, []);

    const fetchRedemptions = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/redemptions`);
            setRedemptions(res.data);
        } catch (error) {
            console.error('Failed to fetch redemptions:', error);
        }
    }, []);

    const fetchPurchases = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/purchases`);
            setPurchases(res.data);
        } catch (error) {
            console.error('Failed to fetch purchases:', error);
        }
    }, []);

    const fetchUsers = useCallback(async () => {
        try {
            const tier = userFilter === 'all' ? undefined : userFilter;
            const res = await axios.get(`${API_URL}/api/admin/users`, { params: { tier } });
            setUsers(res.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    }, [userFilter]);

    const fetchIssues = useCallback(async () => {
        try {
            const status = issueFilter === 'all' ? undefined : issueFilter;
            const res = await axios.get(`${API_URL}/api/admin/issues`, { params: { status } });
            setIssues(res.data);
        } catch (error) {
            console.error('Failed to fetch issues:', error);
        }
    }, [issueFilter]);

    const fetchWaitlist = useCallback(async () => {
        setWaitlistLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/waitlist`);
            setWaitlist(res.data);
        } catch (error) {
            console.error('Failed to fetch waitlist:', error);
        } finally {
            setWaitlistLoading(false);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchStats(),
                fetchPromoCodes(),
                fetchRedemptions(),
                fetchPurchases(),
                fetchUsers(),
                fetchIssues(),
                fetchWaitlist(),
                fetchEmailStats()
            ]);
            setLoading(false);
        };
        loadData();
    }, [fetchStats, fetchPromoCodes, fetchRedemptions, fetchPurchases, fetchUsers, fetchIssues, fetchWaitlist, fetchEmailStats]);

    useEffect(() => {
        fetchUsers();
    }, [userFilter, fetchUsers]);

    useEffect(() => {
        fetchIssues();
    }, [issueFilter, fetchIssues]);

    const handleExportAudit = async () => {
        setExportingAudit(true);
        try {
            const params = {};
            if (auditStartDate) params.start_date = auditStartDate;
            if (auditEndDate) params.end_date = auditEndDate;
            if (auditEmail.trim()) params.email = auditEmail.trim();
            const response = await axios.get(`${API_URL}/api/admin/disclaimer-audit.csv`, {
                params,
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
            const suffix = [
                auditStartDate ? `from-${auditStartDate}` : '',
                auditEndDate ? `to-${auditEndDate}` : '',
                auditEmail ? `email-${auditEmail.trim().replace(/[^a-z0-9]/gi, '_')}` : '',
            ].filter(Boolean).join('_');
            a.download = `disclaimer_audit_${stamp}${suffix ? '_' + suffix : ''}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Audit CSV downloaded');
        } catch (e) {
            const detail = e?.response?.data?.detail || 'Failed to export audit';
            toast.error(detail);
        } finally {
            setExportingAudit(false);
        }
    };

    const handleCreatePromo = async () => {
        if (!newPromoCode) {
            toast.error('Please enter a promo code');
            return;
        }
        setCreatingPromo(true);
        try {
            await axios.post(`${API_URL}/api/admin/promo-codes`, {
                code: newPromoCode.toUpperCase(),
                discount_percent: parseInt(newPromoDiscount) || 0,
                valid_for_tier: newPromoTier === 'all' ? null : newPromoTier,
                max_uses: newPromoMaxUses ? parseInt(newPromoMaxUses) : null
            });
            
            toast.success('Promo code created!');
            setNewPromoCode('');
            setNewPromoDiscount('');
            setNewPromoTier('all');
            setNewPromoMaxUses('');
            fetchPromoCodes();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create promo code');
        } finally {
            setCreatingPromo(false);
        }
    };

    const handleTogglePromo = async (code, isActive) => {
        try {
            await axios.put(`${API_URL}/api/admin/promo-codes/${code}`, {
                is_active: !isActive
            });
            toast.success(isActive ? 'Promo code deactivated' : 'Promo code activated');
            fetchPromoCodes();
        } catch (error) {
            toast.error('Failed to update promo code');
        }
    };

    const handleDeletePromo = async (code) => {
        try {
            await axios.delete(`${API_URL}/api/admin/promo-codes/${code}`);
            toast.success('Promo code deleted');
            fetchPromoCodes();
            fetchStats();
        } catch (error) {
            toast.error('Failed to delete promo code');
        }
    };

    const handleUpdateUserTier = async (userId, newTier) => {
        try {
            await axios.put(`${API_URL}/api/admin/users/${userId}/tier`, null, {
                params: { tier: newTier }
            });
            toast.success('User tier updated');
            fetchUsers();
            fetchStats();
            fetchPurchases();
        } catch (error) {
            toast.error('Failed to update user tier');
        }
    };

    const handleUpdateIssueStatus = async (issueId, newStatus, notes = null) => {
        setUpdatingIssue(issueId);
        try {
            await axios.put(`${API_URL}/api/admin/issues/${issueId}`, {
                status: newStatus,
                admin_notes: notes
            });
            toast.success('Issue status updated');
            fetchIssues();
        } catch (error) {
            toast.error('Failed to update issue');
        } finally {
            setUpdatingIssue(null);
        }
    };

    const handleDeleteIssue = async (issueId) => {
        try {
            await axios.delete(`${API_URL}/api/admin/issues/${issueId}`);
            toast.success('Issue deleted');
            fetchIssues();
        } catch (error) {
            toast.error('Failed to delete issue');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    };

    if (!user?.is_admin) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                <Shield className="w-16 h-16 text-muted-foreground/50 mb-4" />
                <h1 className="font-heading text-2xl font-bold mb-2">Admin Access Required</h1>
                <p className="text-muted-foreground">You don't have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                        <Shield className="w-8 h-8 text-violet-500" />
                        Admin Dashboard
                    </h1>
                    <p className="text-muted-foreground mt-1">Manage promo codes, purchases, and users</p>
                </div>
                <Button variant="outline" onClick={() => {
                    fetchStats();
                    fetchPromoCodes();
                    fetchRedemptions();
                    fetchPurchases();
                    fetchUsers();
                }} className="gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
                </div>
            ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="overview" className="gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Overview
                        </TabsTrigger>
                        <TabsTrigger value="promo-codes" className="gap-2">
                            <Ticket className="w-4 h-4" />
                            Promo Codes
                        </TabsTrigger>
                        <TabsTrigger value="redemptions" className="gap-2">
                            <Gift className="w-4 h-4" />
                            Redemptions
                        </TabsTrigger>
                        <TabsTrigger value="purchases" className="gap-2">
                            <CreditCard className="w-4 h-4" />
                            Purchases
                        </TabsTrigger>
                        <TabsTrigger value="users" className="gap-2">
                            <Users className="w-4 h-4" />
                            Users
                        </TabsTrigger>
                        <TabsTrigger value="issues" className="gap-2">
                            <Bug className="w-4 h-4" />
                            Issues
                        </TabsTrigger>
                        <TabsTrigger value="waitlist" className="gap-2">
                            <ClipboardList className="w-4 h-4" />
                            Waitlist
                        </TabsTrigger>
                        <TabsTrigger value="news" className="gap-2" data-testid="admin-tab-news">
                            <Mail className="w-4 h-4" />
                            News
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="mt-6">
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-3xl font-bold">{stats?.users?.total || 0}</p>
                                    <div className="flex gap-2 mt-2 text-xs">
                                        <Badge variant="outline">{stats?.users?.free || 0} Free</Badge>
                                        <Badge className="bg-teal-500">{stats?.users?.essential || 0} Essential</Badge>
                                        <Badge className="bg-violet-500">{stats?.users?.premium || 0} Premium</Badge>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Founding Members</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-3xl font-bold flex items-center gap-2">
                                        <Crown className="w-6 h-6 text-amber-500" />
                                        {stats?.users?.founding_members || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-2">First 300 users</p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Promo Codes</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-3xl font-bold">{stats?.promo_codes?.total || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        {stats?.promo_codes?.active || 0} active
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Redemptions</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-3xl font-bold">{stats?.redemptions || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-2">Total code uses</p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Waitlist</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-3xl font-bold flex items-center gap-2">
                                        <ClipboardList className="w-6 h-6 text-amber-500" />
                                        {stats?.waitlist || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-2">Professional tier interest</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Email Engagement (Resend webhooks) */}
                        <Card className="mt-6 border-violet-200 dark:border-violet-900 bg-violet-50/40 dark:bg-violet-950/20" data-testid="email-engagement-card">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-violet-600" />
                                    Email Engagement <span className="text-xs font-normal text-muted-foreground">(last 30 days)</span>
                                </CardTitle>
                                <CardDescription>
                                    Real-time delivery, open, and click signal from Resend webhooks — instantly see whether your welcome and reset emails are reaching users.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {!emailStats?.webhook_configured && (
                                    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm flex gap-2" data-testid="webhook-not-configured-warning">
                                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-medium text-amber-900 dark:text-amber-200">Webhook signing secret not configured</p>
                                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                                Resend → Webhooks → Add endpoint pointing to <code className="px-1 bg-amber-100 dark:bg-amber-900 rounded">/api/webhooks/resend</code>, select all <code>email.*</code> events, copy the signing secret into <code>RESEND_WEBHOOK_SECRET</code> in <code>backend/.env</code>, restart backend.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
                                    <div className="rounded-lg bg-white dark:bg-slate-900 border p-3" data-testid="stat-delivered">
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <Send className="w-3.5 h-3.5" /> Delivered
                                        </div>
                                        <p className="text-2xl font-bold mt-1">{emailStats?.delivered ?? '–'}</p>
                                    </div>
                                    <div className="rounded-lg bg-white dark:bg-slate-900 border p-3" data-testid="stat-opened">
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <MailOpen className="w-3.5 h-3.5" /> Opened
                                        </div>
                                        <p className="text-2xl font-bold mt-1">{emailStats?.opened ?? '–'}</p>
                                        {emailStats?.open_rate !== undefined && (
                                            <p className="text-xs text-violet-600 mt-1">{emailStats.open_rate}% open rate</p>
                                        )}
                                    </div>
                                    <div className="rounded-lg bg-white dark:bg-slate-900 border p-3" data-testid="stat-clicked">
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <MousePointer className="w-3.5 h-3.5" /> Clicked
                                        </div>
                                        <p className="text-2xl font-bold mt-1">{emailStats?.clicked ?? '–'}</p>
                                        {emailStats?.click_rate !== undefined && (
                                            <p className="text-xs text-violet-600 mt-1">{emailStats.click_rate}% click rate</p>
                                        )}
                                    </div>
                                    <div className="rounded-lg bg-white dark:bg-slate-900 border p-3" data-testid="stat-bounced">
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <AlertCircle className="w-3.5 h-3.5" /> Bounced / Complained
                                        </div>
                                        <p className="text-2xl font-bold mt-1">{(emailStats?.bounced ?? 0) + (emailStats?.complained ?? 0)}</p>
                                        {emailStats?.bounce_rate !== undefined && (
                                            <p className="text-xs text-rose-600 mt-1">{emailStats.bounce_rate}% bounce rate</p>
                                        )}
                                    </div>
                                </div>

                                {emailStats?.recent_events && emailStats.recent_events.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-xs font-semibold text-muted-foreground mb-2">Recent events</p>
                                        <div className="rounded-lg border bg-white dark:bg-slate-900 max-h-56 overflow-y-auto text-xs">
                                            <table className="w-full">
                                                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800">
                                                    <tr className="text-left">
                                                        <th className="px-3 py-2 font-medium">Event</th>
                                                        <th className="px-3 py-2 font-medium">Recipient</th>
                                                        <th className="px-3 py-2 font-medium">When</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {emailStats.recent_events.slice(0, 15).map((ev) => (
                                                        <tr key={ev.id} className="border-t" data-testid={`event-row-${ev.type}`}>
                                                            <td className="px-3 py-1.5"><span className="font-mono text-[11px]">{(ev.type || '').replace('email.', '')}</span></td>
                                                            <td className="px-3 py-1.5 text-muted-foreground">{ev.recipient}</td>
                                                            <td className="px-3 py-1.5 text-muted-foreground">{ev.received_at ? new Date(ev.received_at).toLocaleString() : '–'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Legal Compliance */}
                        <Card className="mt-6 border-emerald-200 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/20" data-testid="legal-compliance-card">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                                    Legal Compliance
                                </CardTitle>
                                <CardDescription>
                                    Audit trail of every user's disclaimer acceptances with timestamps and device info — useful for App Store review, SOC-2 / HIPAA-readiness audits, and "did the user actually agree?" disputes.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-3 sm:grid-cols-3">
                                    <div>
                                        <Label className="text-xs">Start date <span className="text-muted-foreground">(optional)</span></Label>
                                        <Input
                                            type="date"
                                            value={auditStartDate}
                                            onChange={(e) => setAuditStartDate(e.target.value)}
                                            data-testid="audit-start-date"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">End date <span className="text-muted-foreground">(optional)</span></Label>
                                        <Input
                                            type="date"
                                            value={auditEndDate}
                                            onChange={(e) => setAuditEndDate(e.target.value)}
                                            data-testid="audit-end-date"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Email contains <span className="text-muted-foreground">(optional)</span></Label>
                                        <Input
                                            type="text"
                                            placeholder="e.g. amber or @gmail.com"
                                            value={auditEmail}
                                            onChange={(e) => setAuditEmail(e.target.value)}
                                            data-testid="audit-email-filter"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap">
                                    <Button
                                        onClick={handleExportAudit}
                                        disabled={exportingAudit}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        data-testid="export-disclaimer-audit-btn"
                                    >
                                        {exportingAudit ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating CSV…</>
                                        ) : (
                                            <><FileDown className="w-4 h-4 mr-2" /> Export Disclaimer Audit (CSV)</>
                                        )}
                                    </Button>
                                    {(auditStartDate || auditEndDate || auditEmail) && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => { setAuditStartDate(''); setAuditEndDate(''); setAuditEmail(''); }}
                                            data-testid="audit-clear-filters"
                                        >
                                            Clear filters
                                        </Button>
                                    )}
                                </div>

                                <p className="text-xs text-muted-foreground">
                                    Columns: user_id · user_email · user_name · disclaimer_type · version · accepted_at · user_agent · platform · language · screen · app_version · accepted_via
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Promo Codes Tab */}
                    <TabsContent value="promo-codes" className="mt-6 space-y-6">
                        {/* Create New Promo */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Create New Promo Code</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid gap-4 md:grid-cols-5">
                                    <div>
                                        <Label>Code</Label>
                                        <Input
                                            placeholder="WELCOME30"
                                            value={newPromoCode}
                                            onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                                            className="uppercase"
                                        />
                                    </div>
                                    <div>
                                        <Label>Discount %</Label>
                                        <Input
                                            type="number"
                                            placeholder="30"
                                            value={newPromoDiscount}
                                            onChange={(e) => setNewPromoDiscount(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label>Valid For</Label>
                                        <Select value={newPromoTier} onValueChange={setNewPromoTier}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Tiers</SelectItem>
                                                <SelectItem value="essential">Essential Only</SelectItem>
                                                <SelectItem value="premium">Premium Only</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Max Uses</Label>
                                        <Input
                                            type="number"
                                            placeholder="Unlimited"
                                            value={newPromoMaxUses}
                                            onChange={(e) => setNewPromoMaxUses(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <Button onClick={handleCreatePromo} disabled={creatingPromo} className="w-full gap-2">
                                            {creatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                            Create
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Promo Codes List */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">All Promo Codes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {promoCodes.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">No promo codes yet</p>
                                ) : (
                                    <div className="space-y-3">
                                        {promoCodes.map((promo) => (
                                            <div
                                                key={promo.code}
                                                className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="flex items-center gap-2">
                                                        <code className="text-lg font-mono font-bold">{promo.code}</code>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => copyToClipboard(promo.code)}
                                                        >
                                                            <Copy className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                    <Badge className={promo.is_active ? 'bg-emerald-500' : 'bg-gray-500'}>
                                                        {promo.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <Percent className="w-4 h-4 text-muted-foreground" />
                                                        {promo.discount_percent}% off
                                                    </div>
                                                    {promo.valid_for_tier && (
                                                        <Badge variant="outline">{promo.valid_for_tier}</Badge>
                                                    )}
                                                    <div className="text-sm text-muted-foreground">
                                                        {promo.redemption_count || 0} uses
                                                        {promo.max_uses && ` / ${promo.max_uses}`}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={promo.is_active}
                                                        onCheckedChange={() => handleTogglePromo(promo.code, promo.is_active)}
                                                    />
                                                    <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="text-destructive">
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogContent>
                                                            <AlertDialogHeader>
                                                                <AlertDialogTitle>Delete Promo Code</AlertDialogTitle>
                                                                <AlertDialogDescription>
                                                                    Are you sure you want to delete "{promo.code}"? This cannot be undone.
                                                                </AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                <AlertDialogAction onClick={() => handleDeletePromo(promo.code)}>
                                                                    Delete
                                                                </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialog>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Redemptions Tab */}
                    <TabsContent value="redemptions" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Recent Redemptions</CardTitle>
                                <CardDescription>Promo codes that have been used</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {redemptions.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">No redemptions yet</p>
                                ) : (
                                    <div className="space-y-3">
                                        {redemptions.map((redemption) => (
                                            <div
                                                key={redemption.id}
                                                className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <Gift className="w-5 h-5 text-violet-500" />
                                                    <div>
                                                        <p className="font-medium">{redemption.user_name}</p>
                                                        <p className="text-sm text-muted-foreground">{redemption.user_email}</p>
                                                    </div>
                                                    <code className="font-mono bg-muted px-2 py-1 rounded">{redemption.promo_code}</code>
                                                    <Badge>{redemption.discount_percent}% off</Badge>
                                                    <Badge variant="outline">{redemption.tier}</Badge>
                                                </div>
                                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                                    <Clock className="w-4 h-4" />
                                                    {formatDate(redemption.redeemed_at)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Purchases Tab */}
                    <TabsContent value="purchases" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Recent Purchases</CardTitle>
                                <CardDescription>Subscription upgrades and manual grants</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {purchases.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">No purchases yet</p>
                                ) : (
                                    <div className="space-y-3">
                                        {purchases.map((purchase) => (
                                            <div
                                                key={purchase.id}
                                                className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <CreditCard className="w-5 h-5 text-emerald-500" />
                                                    <div>
                                                        <p className="font-medium">{purchase.user_name}</p>
                                                        <p className="text-sm text-muted-foreground">{purchase.user_email}</p>
                                                    </div>
                                                    <Badge className={purchase.tier === 'premium' ? 'bg-violet-500' : 'bg-teal-500'}>
                                                        {purchase.tier}
                                                    </Badge>
                                                    {purchase.type === 'admin_grant' && (
                                                        <Badge variant="outline">Admin Grant</Badge>
                                                    )}
                                                    {purchase.amount > 0 && (
                                                        <span className="text-sm font-medium">${purchase.amount}</span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                                    <Clock className="w-4 h-4" />
                                                    {formatDate(purchase.created_at)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Users Tab */}
                    <TabsContent value="users" className="mt-6 space-y-4">
                        <div className="flex items-center gap-4">
                            <Label>Filter by tier:</Label>
                            <Select value={userFilter} onValueChange={setUserFilter}>
                                <SelectTrigger className="w-40">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Users</SelectItem>
                                    <SelectItem value="free">Free</SelectItem>
                                    <SelectItem value="essential">Essential</SelectItem>
                                    <SelectItem value="premium">Premium</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Users ({users.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {users.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">No users found</p>
                                ) : (
                                    <div className="space-y-3">
                                        {users.map((u) => (
                                            <div
                                                key={u.id}
                                                className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-semibold">
                                                        {u.name?.charAt(0).toUpperCase() || 'U'}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium flex items-center gap-2">
                                                            {u.name}
                                                            {u.is_founding_member && <Crown className="w-4 h-4 text-amber-500" />}
                                                            {u.is_admin && <Shield className="w-4 h-4 text-violet-500" />}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">{u.email}</p>
                                                    </div>
                                                    <Badge className={
                                                        u.subscription_tier === 'premium' ? 'bg-violet-500' :
                                                        u.subscription_tier === 'essential' ? 'bg-teal-500' : ''
                                                    }>
                                                        {u.subscription_tier}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-sm text-muted-foreground">
                                                        Joined {formatDate(u.created_at)}
                                                    </div>
                                                    <Select
                                                        value={u.subscription_tier}
                                                        onValueChange={(newTier) => handleUpdateUserTier(u.id, newTier)}
                                                    >
                                                        <SelectTrigger className="w-32">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="free">Free</SelectItem>
                                                            <SelectItem value="essential">Essential</SelectItem>
                                                            <SelectItem value="premium">Premium</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Issues Tab */}
                    <TabsContent value="issues" className="mt-6 space-y-4">
                        <div className="flex items-center gap-4">
                            <Label>Filter by status:</Label>
                            <Select value={issueFilter} onValueChange={setIssueFilter}>
                                <SelectTrigger className="w-40">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Issues</SelectItem>
                                    <SelectItem value="open">Open</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="resolved">Resolved</SelectItem>
                                    <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Reported Issues ({issues.length})</CardTitle>
                                <CardDescription>User-reported bugs, feature requests, and questions</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {issues.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">No issues reported</p>
                                ) : (
                                    <div className="space-y-4">
                                        {issues.map((issue) => {
                                            const statusColors = {
                                                open: 'bg-amber-500',
                                                in_progress: 'bg-blue-500',
                                                resolved: 'bg-emerald-500',
                                                closed: 'bg-gray-500'
                                            };
                                            const categoryIcons = {
                                                bug: Bug,
                                                feature: MessageSquare,
                                                question: MessageSquare,
                                                other: MessageSquare
                                            };
                                            const CategoryIcon = categoryIcons[issue.category] || Bug;
                                            
                                            return (
                                                <div
                                                    key={issue.id}
                                                    className="p-4 rounded-lg border bg-card"
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex items-start gap-3 min-w-0 flex-1">
                                                            <CategoryIcon className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <p className="font-semibold">{issue.title}</p>
                                                                    <Badge variant="outline" className="text-xs">{issue.category}</Badge>
                                                                    <Badge className={`${statusColors[issue.status]} text-white text-xs`}>
                                                                        {issue.status.replace('_', ' ')}
                                                                    </Badge>
                                                                </div>
                                                                <p className="text-sm text-muted-foreground mt-1">
                                                                    {issue.description}
                                                                </p>
                                                                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                                    <span>From: {issue.user_name} ({issue.user_email})</span>
                                                                    <span>{formatDate(issue.created_at)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <Select
                                                                value={issue.status}
                                                                onValueChange={(newStatus) => handleUpdateIssueStatus(issue.id, newStatus)}
                                                                disabled={updatingIssue === issue.id}
                                                            >
                                                                <SelectTrigger className="w-32">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="open">Open</SelectItem>
                                                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                                                    <SelectItem value="resolved">Resolved</SelectItem>
                                                                    <SelectItem value="closed">Closed</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            <AlertDialog>
                                                                <AlertDialogTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="text-destructive">
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </AlertDialogTrigger>
                                                                <AlertDialogContent>
                                                                    <AlertDialogHeader>
                                                                        <AlertDialogTitle>Delete Issue</AlertDialogTitle>
                                                                        <AlertDialogDescription>
                                                                            Are you sure you want to delete this issue? This cannot be undone.
                                                                        </AlertDialogDescription>
                                                                    </AlertDialogHeader>
                                                                    <AlertDialogFooter>
                                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                        <AlertDialogAction onClick={() => handleDeleteIssue(issue.id)}>
                                                                            Delete
                                                                        </AlertDialogAction>
                                                                    </AlertDialogFooter>
                                                                </AlertDialogContent>
                                                            </AlertDialog>
                                                        </div>
                                                    </div>
                                                    {issue.admin_notes && (
                                                        <div className="mt-3 p-2 rounded bg-primary/5 border border-primary/20">
                                                            <p className="text-xs font-medium text-primary">Admin Notes:</p>
                                                            <p className="text-xs text-muted-foreground">{issue.admin_notes}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Waitlist Tab */}
                    <TabsContent value="waitlist" className="mt-6 space-y-4">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Professional Tier Waitlist</CardTitle>
                                        <CardDescription>
                                            Users interested in the Professional tier ({waitlist.length} entries)
                                        </CardDescription>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={fetchWaitlist}
                                        disabled={waitlistLoading}
                                    >
                                        <RefreshCw className={`w-4 h-4 mr-2 ${waitlistLoading ? 'animate-spin' : ''}`} />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {waitlist.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>No waitlist entries yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {waitlist.map((entry) => (
                                            <div
                                                key={entry.id}
                                                className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                                                        <Mail className="w-5 h-5 text-amber-500" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{entry.name}</p>
                                                        <p className="text-sm text-muted-foreground">{entry.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-right">
                                                        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                                                            Professional
                                                        </Badge>
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            {format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}
                                                        </p>
                                                    </div>
                                                    <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogContent>
                                                            <AlertDialogHeader>
                                                                <AlertDialogTitle>Remove from Waitlist</AlertDialogTitle>
                                                                <AlertDialogDescription>
                                                                    Are you sure you want to remove {entry.name} ({entry.email}) from the waitlist?
                                                                </AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                <AlertDialogAction
                                                                    onClick={async () => {
                                                                        try {
                                                                            await axios.delete(`${API_URL}/api/admin/waitlist/${entry.id}`);
                                                                            toast.success('Removed from waitlist');
                                                                            fetchWaitlist();
                                                                        } catch (error) {
                                                                            toast.error('Failed to remove from waitlist');
                                                                        }
                                                                    }}
                                                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                                >
                                                                    Remove
                                                                </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialog>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* News Tab */}
                    <TabsContent value="news" className="mt-6">
                        <AdminNewsTab />
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
}
